import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

const GAP = 6;
const GUTTER = 12;

/**
 * A dropdown panel portalled to <body> and positioned against its trigger.
 *
 * The sidebar is a 240px scroller, and anything absolutely positioned inside
 * it is clipped at its edge — which is most of a 288px project list. In the
 * body the panel can be as wide as it needs, still opens over the mobile
 * drawer, and flips above the trigger when there's no room below.
 */
export function AnchoredPanel({
  anchorRef,
  open,
  onClose,
  width = 288,
  className,
  children,
}: {
  anchorRef: RefObject<HTMLElement>;
  open: boolean;
  onClose: () => void;
  width?: number;
  className?: string;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      if (!a) return;
      const below = window.innerHeight - a.bottom - GAP - GUTTER;
      const above = a.top - GAP - GUTTER;
      const flip = below < 240 && above > below;
      const maxHeight = Math.max(160, flip ? above : below);
      const left = Math.min(Math.max(GUTTER, a.left), window.innerWidth - width - GUTTER);
      const height = panelRef.current?.offsetHeight ?? 0;
      const top = flip ? Math.max(GUTTER, a.top - GAP - Math.min(height, maxHeight)) : a.bottom + GAP;
      setPos({ top, left, maxHeight });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef, width]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      onClose();
      anchorRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return createPortal(
    <div
      ref={panelRef}
      style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? 0, width, maxHeight: pos?.maxHeight, zIndex: 60 }}
      className={cn('flex flex-col overflow-hidden rounded-xl border border-grey-200 bg-white shadow-elevated', className)}
    >
      {children}
    </div>,
    document.body,
  );
}
